/**
 * Follow-ups on a Care Concierge case (Feature 106, requirement v4 item 10).
 *
 * Rows, not a field: a case can carry several, and the requirement is explicit that they are
 * "multiple entries, not a single date". Each one saves on its own for the same reason the care
 * plan does — they are additions to a record, not edits held in a form until someone presses Save.
 *
 * Removal exists because the dates are "always editable" and a follow-up is a row: editing a wrong
 * one means deleting it. Without that a mistyped date sits on the case for good.
 */

import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { useToast } from '@/shared/components/Toast';
import {
  addCaseFollowUp,
  removeCaseFollowUp,
  type CaseFollowUp,
} from '../../services/careConciergeCasesApi';

const errorMessage = (error: unknown, fallback: string): string =>
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
  (error as { message?: string })?.message ??
  fallback;

/**
 * follow_up_on is a DATE column, not a timestamp: it means a calendar day, not an instant. Prisma
 * hands it over as UTC midnight, so formatting it in the reader's timezone moves it a day back for
 * anyone west of UTC — a follow-up booked for Oct 1 would read Sep 30 in São Paulo. Pinned to UTC
 * so the day shown is the day stored.
 */
const formatDate = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString('en-US', {
        timeZone: 'UTC',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
};

type CaseFollowUpsProps = {
  caseId: string;
  followUps: CaseFollowUp[];
  /** Hands back the case the server returned, so the list reflects the write rather than a guess. */
  onChanged: (followUps: CaseFollowUp[]) => void;
};

export const CaseFollowUps = ({ caseId, followUps, onChanged }: CaseFollowUpsProps) => {
  const { showToast } = useToast();
  const [date, setDate] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const handleAdd = async () => {
    if (!date) {
      showToast('Pick a date for the follow-up.', 'error');
      return;
    }

    setBusy(true);
    try {
      const updated = await addCaseFollowUp(
        caseId,
        new Date(date).toISOString(),
        note.trim() || undefined,
      );
      onChanged(updated.followUps);
      setDate('');
      setNote('');
    } catch (error) {
      showToast(errorMessage(error, 'Could not add the follow-up.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (followUpId: string) => {
    setBusy(true);
    try {
      const updated = await removeCaseFollowUp(caseId, followUpId);
      onChanged(updated.followUps);
    } catch (error) {
      showToast(errorMessage(error, 'Could not remove the follow-up.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Label htmlFor="cc-case-follow-up-date">Follow-ups</Label>

      {followUps.length > 0 && (
        <ul className="mt-1 space-y-1">
          {followUps.map((followUp) => (
            <li
              key={followUp.id}
              className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-1.5 text-sm"
            >
              <span>
                {formatDate(followUp.followUpOn)}
                {followUp.note && (
                  <span className="ml-2 text-muted-foreground">{followUp.note}</span>
                )}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`Remove follow-up on ${formatDate(followUp.followUpOn)}`}
                onClick={() => handleRemove(followUp.id)}
                disabled={busy}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-2 flex flex-wrap items-end gap-2">
        <div className="w-40">
          <Input
            id="cc-case-follow-up-date"
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            disabled={busy}
          />
        </div>
        <div className="min-w-[10rem] flex-1">
          <Input
            aria-label="Follow-up note (optional)"
            placeholder="Note (optional)"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            disabled={busy}
          />
        </div>
        <Button type="button" variant="outline" size="sm" onClick={handleAdd} disabled={busy}>
          Add follow-up
        </Button>
      </div>
    </div>
  );
};
